import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Gauge, Loader2, PackagePlus, Check } from 'lucide-react';

const URGENCY_TONES = {
  critical: 'border-rose-200 bg-rose-50 text-rose-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
  info: 'border-sky-200 bg-sky-50 text-sky-800',
};

const urgencyFor = (daysLeft) => (daysLeft <= 2 ? 'critical' : daysLeft <= 4 ? 'warning' : 'info');

export const ShortageForecast = ({ onDrafted }) => {
  const [data, setData] = useState(null);
  const [drafting, setDrafting] = useState(null);
  const [drafted, setDrafted] = useState({});

  const load = () => api.get('/duti/forecast').then(({ data }) => {
    setData(data);
    const seeded = {};
    for (const f of data.forecasts || []) {
      if (f.drafted_action_id) seeded[f.product_id] = f.drafted_action_id;
    }
    setDrafted(seeded);
  }).catch(() => setData({ forecasts: [] }));

  useEffect(() => { load(); }, []);

  const draftPo = async (f) => {
    setDrafting(f.product_id);
    try {
      const { data } = await api.post(`/duti/forecast/${f.product_id}/draft-po`);
      setDrafted((prev) => ({ ...prev, [f.product_id]: data.action_id }));
      toast.success(`Draft PO sent to the approval queue for ${f.product_name}`);
      onDrafted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not draft a purchase order for this item');
    } finally {
      setDrafting(null);
    }
  };

  if (!data) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;
  const forecasts = data.forecasts || [];

  return (
    <section className="rounded-2xl border bg-gradient-to-br from-sky-50 to-background p-6 space-y-3" data-testid="duti-shortage-forecast">
      <header className="flex items-center gap-3">
        <Gauge className="h-6 w-6 text-sky-600" />
        <div>
          <h2 className="text-lg font-semibold">7-day shortage forecast</h2>
          <p className="text-sm text-muted-foreground" data-testid="duti-forecast-summary">
            {forecasts.length
              ? `${forecasts.length} product(s) trending toward a stockout, based on the last 14 days of sales`
              : 'No products are trending toward a stockout in the next week.'}
          </p>
        </div>
      </header>

      {forecasts.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="duti-forecast-empty">Nothing urgent — check back after tomorrow's sales.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {forecasts.map((f) => {
            const tone = urgencyFor(f.days_left);
            const draftedActionId = drafted[f.product_id];
            return (
              <div key={f.product_id} className={`flex flex-col gap-1.5 px-3 py-2.5 rounded-xl border shadow-sm text-xs ${URGENCY_TONES[tone]}`}
                data-testid={`duti-forecast-row-${f.product_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold leading-tight truncate">{f.product_name}</p>
                    <p className="opacity-75 leading-tight mt-0.5">
                      {f.stock_on_hand} on hand · selling ~{f.daily_velocity}/day
                    </p>
                    {f.already_low_stock && (
                      <p className="opacity-60 leading-tight mt-0.5">Already in today's restock briefing</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold" data-testid={`duti-forecast-days-${f.product_id}`}>
                      ~{f.days_left}d left
                    </p>
                    <p className="opacity-70">{f.projected_stockout_date}</p>
                  </div>
                </div>
                {draftedActionId ? (
                  <p className="flex items-center gap-1 text-[11px] font-semibold opacity-80" data-testid={`duti-forecast-drafted-${f.product_id}`}>
                    <Check className="h-3 w-3" />Sent to approval queue
                  </p>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-[10px] gap-1 self-start bg-background/70"
                    disabled={drafting === f.product_id} onClick={() => draftPo(f)}
                    data-testid={`duti-forecast-draft-${f.product_id}`}>
                    {drafting === f.product_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PackagePlus className="h-3 w-3" />}
                    Draft PO
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
